import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Camera, Lock, AlertCircle, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';

const ForceChangePasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, forcePasswordChange, needsPasswordChange, logout } = useAuth();
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Prüfe ob Benutzer diese Seite besuchen darf
  useEffect(() => {
    if (!user) {
      navigate('/admin/login');
      return;
    }
    
    // Wenn keine erzwungene Änderung nötig ist und nicht von Login weitergeleitet
    if (!needsPasswordChange() && !location.state?.forced) {
      navigate('/admin');
    }
  }, [user, needsPasswordChange, navigate, location.state]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validierung
    if (newPassword.length < 6) {
      setError('Das Passwort muss mindestens 6 Zeichen haben.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Die Passwörter stimmen nicht überein.');
      return;
    }

    setLoading(true);
    try {
      await forcePasswordChange(newPassword);
      toast.success('Passwort erfolgreich geändert');
      navigate('/admin');
    } catch (err) {
      console.error('Password change failed:', err);
      setError('Passwort konnte nicht geändert werden. Bitte versuchen Sie es erneut.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-slate-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center">
            <Camera className="w-7 h-7 text-white" />
          </div>
          <span className="font-bold text-2xl text-slate-800">GB Sylt</span>
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-amber-600" />
            </div>
            <CardTitle className="text-2xl heading-app">Passwort ändern</CardTitle>
            <CardDescription>
              Ihr Administrator hat ein Ersatzpasswort für Sie erstellt. 
              Bitte vergeben Sie jetzt ein neues persönliches Passwort.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="newPassword">Neues Passwort</Label>
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mindestens 6 Zeichen"
                  required
                  autoComplete="new-password"
                  data-testid="new-password-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Passwort bestätigen</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Passwort wiederholen"
                  required
                  autoComplete="new-password"
                  data-testid="confirm-password-input"
                />
              </div>

              {newPassword && confirmPassword && newPassword === confirmPassword && (
                <div className="flex items-center gap-2 text-sm text-green-600">
                  <Check className="w-4 h-4" />
                  Passwörter stimmen überein
                </div>
              )}

              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading}
                data-testid="change-password-button"
              >
                {loading ? 'Wird gespeichert...' : 'Passwort speichern'}
              </Button>
            </form>

            <div className="mt-6 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={handleLogout}
                className="text-sm text-slate-500 hover:text-slate-700 w-full text-center"
              >
                Abmelden und später fortfahren
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForceChangePasswordPage;
